import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { PageHero, PrimaryButton, Reveal, SectionTag } from "@/components/ui-bits";
import api from "@/lib/api";

const IndustryDetail = () => {
  const { slug } = useParams();
  const [industry, setIndustry] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/industries/${slug}`)
      .then((r) => setIndustry(r.data))
      .catch(() => setIndustry(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-tark-dim">Loading…</div>;
  if (!industry) return <Navigate to="/industries" replace />;

  return (
    <>
      <PageHero
        eyebrow={`Industry — ${industry.name}`}
        lines={[industry.headline || industry.name]}
        sub={industry.description}
      >
        <PrimaryButton to="/start-a-project" testId="industry-talk-expert">Talk to an Expert</PrimaryButton>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 pb-28 lg:px-10" data-testid="industry-detail">
        <div className="grid gap-5 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-tark-line bg-tark-card p-8 sm:p-10">
              <SectionTag>The Challenges</SectionTag>
              <h2 className="mt-4 font-display text-xl font-bold sm:text-2xl">What holds teams back</h2>
              <ul className="mt-7 space-y-4" data-testid="industry-challenges">
                {industry.challenges?.map((c) => (
                  <li key={c} className="flex items-start gap-3 text-sm leading-relaxed text-tark-mut">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-tark-blue" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-full rounded-2xl border border-tark-blue/40 bg-gradient-to-br from-tark-card to-[#0A1A33] p-8 sm:p-10">
              <SectionTag>What TARK Delivers</SectionTag>
              <h2 className="mt-4 font-display text-xl font-bold sm:text-2xl">Engineering answers</h2>
              <ul className="mt-7 space-y-4" data-testid="industry-offerings">
                {industry.offerings?.map((o) => (
                  <li key={o} className="flex items-start gap-3 text-sm leading-relaxed text-tark-mut">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-tark-cyan" />
                    {o}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-16 rounded-2xl border border-tark-line bg-tark-surface/60 p-10 text-center sm:p-14">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Discuss Your Project</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm text-tark-mut">
            Tell us about your {industry.name.toLowerCase()} environment and what you need to achieve.
          </p>
          <div className="mt-8">
            <PrimaryButton to="/start-a-project" testId="industry-bottom-cta">Request Consultation</PrimaryButton>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default IndustryDetail;
